/**
 * `CellSetCoordinator`: the one writer per logical cell that a client's sheet
 * accessors share. It orders each cell's checked writes (one in flight, the
 * newest pending one per accessor kept), issues and settles the write
 * tickets, and stamps the sends so a row shows the newest settled value.
 * Its state is the per-cell queues, send stamps and tickets; it knows an
 * accessor only by identity. `CellAccessor` (`./sheet-cell-accessor.ts`)
 * owns one per client (`SheetHandleDeps.cellSetCoordinator`) and
 * re-exports it.
 *
 * @module
 */

import type { Option } from "@tento-lona/sheets";

// One CellAccessor.set writer per logical cell, shared by one client's sheet
// accessors (an owner below is the accessor, by identity). Keep the in-flight write's own verdict. Adjacent pending edits
// from the same accessor collapse to its latest value; covered callers await
// that checked write's verdict. Different accessors retain their own
// mirror/notification steps in FIFO order. A one-editor burst with its first
// POST held can use just two wire writes.
type CellSetWaiter = {
  readonly resolve: () => void;
  readonly reject: (reason: unknown) => void;
};
type CellSetJob = {
  /** The accessor that issued the write; compared by identity only. */
  readonly owner: object;
  write: () => Promise<void>;
  /** The issue ticket of the call whose `write` the job runs: superseded when a newer call of the same owner replaces it. */
  ticket: Option<CellSetCoordinator.Ticket>;
  readonly waiters: CellSetWaiter[];
};
type CellSetQueue = { readonly pending: CellSetJob[] };

/**
 * One cell's writes as the coordinator orders their mirrors: `shown` is the
 * send stamp of the write whose value the row shows, `open` counts the
 * writes not yet settled.
 */
type CellSends = { shown: number; open: number };

export class CellSetCoordinator {
  readonly #queues = new Map<string, CellSetQueue>();
  readonly #sends = new Map<string, CellSends>();
  #stamps = 0;
  /**
   * The issue tickets of each cell's writes not yet done (`issue`): a
   * checked write's is done once it reached the wire (or will not), a
   * durable write's once its handoff settled. Issue order across the two
   * kinds: a durable write waits for the checked writes issued before it to
   * be sent (`olderSent`), a checked write for the durable writes issued
   * before it to settle (`olderSent` too) — never for a newer one, so
   * neither waits on the other in a loop.
   */
  readonly #tickets = new Map<string, Set<CellSetCoordinator.Ticket>>();
  #issued = 0;

  /**
   * Take the issue ticket of a write of the cell `key` — at the call, before
   * anything is awaited, so the order is the callers' issue order. Mark it
   * `done` once the write reached the wire (`stamp`), will not (it was
   * superseded or failed first), or — a durable write — its handoff settled.
   */
  issue(key: string, kind: "checked" | "durable"): CellSetCoordinator.Ticket {
    let finish: () => void = () => {};
    const done = new Promise<void>((resolve) => {
      finish = resolve;
    });
    const ticket: CellSetCoordinator.Ticket = { key, n: ++this.#issued, kind, done, finish, finished: false };
    let tickets = this.#tickets.get(key);
    if (tickets == null) {
      tickets = new Set();
      this.#tickets.set(key, tickets);
    }
    tickets.add(ticket);
    return ticket;
  }

  /** `ticket`'s write is done (see `issue`); twice changes nothing. */
  done(ticket: CellSetCoordinator.Ticket): void {
    if (ticket.finished) return;
    ticket.finished = true;
    ticket.finish();
    const tickets = this.#tickets.get(ticket.key);
    if (tickets == null) return;
    tickets.delete(ticket);
    if (tickets.size === 0) this.#tickets.delete(ticket.key);
  }

  /**
   * Resolves once every write of `ticket`'s cell of the OTHER kind issued
   * before it is done: for a durable write, the older checked writes have
   * reached the wire; for a checked write, the older durable writes'
   * handoffs settled (the executor may send them later still).
   */
  async olderSent(ticket: CellSetCoordinator.Ticket): Promise<void> {
    const older = [...(this.#tickets.get(ticket.key) ?? [])].filter((t) => t.kind !== ticket.kind && t.n < ticket.n);
    if (older.length === 0) return;
    await Promise.all(older.map((t) => t.done));
  }

  /**
   * Open one write of the cell `key` — a checked `set` or a durable
   * `setDurable` of one date-keyed cell share it, so their mirrors are
   * ordered by when each reached the wire (`stamp`), not by when each
   * answered: the server applies the one sent last, and so does the row.
   * Close it with `close` whatever its outcome.
   */
  open(key: string): CellSetCoordinator.Send {
    let sends = this.#sends.get(key);
    if (sends == null) {
      sends = { shown: 0, open: 0 };
      this.#sends.set(key, sends);
    }
    sends.open++;
    return { key, stamp: 0 };
  }

  /** Stamp `send` as reaching the wire now (a checked POST sent, a durable
   *  write handed to the executor). */
  stamp(send: CellSetCoordinator.Send): void {
    send.stamp = ++this.#stamps;
  }

  /** Whether `send`'s value may be mirrored: no write of the cell sent
   *  after it was mirrored already. Claims the cell when so. */
  mayShow(send: CellSetCoordinator.Send): boolean {
    const sends = this.#sends.get(send.key);
    if (sends == null) return true;
    if (send.stamp < sends.shown) return false;
    sends.shown = send.stamp;
    return true;
  }

  /** Close `send` (opened by `open`). */
  close(send: CellSetCoordinator.Send): void {
    const sends = this.#sends.get(send.key);
    if (sends == null) return;
    if (--sends.open <= 0) this.#sends.delete(send.key);
  }

  /**
   * Run `write` — one checked write of the cell `key` by `owner` — after the
   * cell's writes queued before it. A pending (not yet running) write of the
   * same owner is replaced by it: the older call's `ticket` is done then
   * (that value never reaches the wire), and its callers await this one.
   */
  serialize(
    key: string,
    owner: object,
    write: () => Promise<void>,
    ticket: Option<CellSetCoordinator.Ticket>,
  ): Promise<void> {
    let queue = this.#queues.get(key);
    const created = queue == null;
    if (queue == null) {
      queue = { pending: [] };
      this.#queues.set(key, queue);
    }
    const ownedQueue = queue;
    const run = (job: CellSetJob): void => {
      Promise.resolve().then(job.write).then(
        () => settle(job, true, null),
        (error: unknown) => settle(job, false, error),
      );
    };
    const settle = (job: CellSetJob, accepted: boolean, error: unknown): void => {
      for (const waiter of job.waiters) {
        if (accepted) waiter.resolve();
        else waiter.reject(error);
      }
      const next = ownedQueue.pending.shift();
      if (next != null) {
        run(next);
      } else {
        this.#queues.delete(key);
      }
    };
    return new Promise<void>((resolve, reject) => {
      const waiter = { resolve, reject };
      const last = ownedQueue.pending.at(-1);
      if (last?.owner === owner) {
        if (last.ticket != null) this.done(last.ticket);
        last.write = write;
        last.ticket = ticket;
        last.waiters.push(waiter);
      } else if (created) {
        run({ owner, write, ticket, waiters: [waiter] });
      } else {
        ownedQueue.pending.push({ owner, write, ticket, waiters: [waiter] });
      }
    });
  }
}

export namespace CellSetCoordinator {
  /** One write of a cell (`CellSetCoordinator.open`): `stamp` is its send
   *  order (`0`: not sent). */
  export interface Send {
    readonly key: string;
    stamp: number;
  }

  /** A write's issue ticket (`CellSetCoordinator.issue`): its cell, its issue number, its kind, and when it is done. */
  export interface Ticket {
    readonly key: string;
    readonly n: number;
    readonly kind: "checked" | "durable";
    readonly done: Promise<void>;
    readonly finish: () => void;
    finished: boolean;
  }
}
